/**
 * src/components/public/layout/Logo.tsx
 *
 * SecureSys brand logosu — light/dark theme'e gore farkli dosya gosterir.
 * Server Component: useTheme() yerine CSS class strategy kullanir.
 * `<html class="dark">` selector'i ile dark logo gozukur, aksi halde light logo.
 *
 * Anasayfa link'i (locale-aware) icine sarilmistir; header'da ve footer'da
 * iki yerde reuse edilir.
 *
 * Logo natural aspect ratio: ~2.32:1 (1279x551 dark, 1650x711 white).
 * width/height prop'larini bu orana yakin verirsen aspect bozulmaz.
 */

import { MediaImage as Image } from '@/components/shared/MediaImage'

import { siteSettings } from '@/data/site-settings'
import { Link } from '@/lib/i18n/navigation'

/**
 * Logo natural aspect ratio (1279/551 ~= 2.32). Yeni width belirlerken
 * height = round(width / LOGO_ASPECT) kullan ki next/image warning olmasin.
 */
export const LOGO_ASPECT = 1279 / 551

interface LogoProps {
  /**
   * Logo width (px). Header'da 140, footer'da 160 tipik.
   * Height otomatik LOGO_ASPECT'e gore hesaplanir.
   */
  width: number
  /**
   * next/image priority — header'da above-the-fold LCP icin true,
   * footer'da false (default).
   */
  priority?: boolean
  /**
   * Ek Tailwind class'lari (orn. responsive max-h).
   */
  className?: string
  /**
   * Logo'nun sarmaladığı home link'in aria-label'i (i18n string).
   *
   * **Neden prop**: Logo hem Server (HeaderMainNav, SiteFooter) hem Client
   * (MobileMenuDrawer 'use client') tree'lerden render edilir. Önceki sürüm
   * içinde `await getTranslations(...)` çağrısı vardı; Client tree'de
   * "getTranslations is not supported in Client Components" hatasına neden
   * oluyordu (React #482 boundary trigger). Bu sürümde label dışarıdan
   * verilir — caller kendi context'ine göre (useTranslations veya
   * getTranslations) çevirir.
   */
  homeAriaLabel: string
}

/**
 * Logo — locale-aware home link'i sarmalayan brand image.
 *
 * Light/dark theme switching CSS-only: light logo `dark:hidden`, dark logo
 * `hidden dark:block`. Bu sayede component RSC kalir, hydration mismatch yok.
 * Translation bağımlılığı yok — caller `homeAriaLabel` ile çevirir, böylece
 * hem Server hem Client tree'lerden güvenle render edilebilir.
 *
 * @param width Logo width (px); height aspect ratio ile otomatik
 * @param priority next/image priority (true sadece header LCP icin)
 * @param className Ek Tailwind sinifları (opsiyonel)
 * @param homeAriaLabel Home link aria-label — caller çevirir (i18n)
 */
export function Logo({ width, priority = false, className = '', homeAriaLabel }: LogoProps) {
  const { logoLight, logoDark } = siteSettings.brand
  const height = Math.round(width / LOGO_ASPECT)

  return (
    <Link
      href="/"
      aria-label={homeAriaLabel}
      className={`inline-flex items-center ${className}`}
    >
      {/* Light theme logo — koyu yazili, beyaz/acik bg uzerinde okunur */}
      <Image
        src={logoLight}
        alt={siteSettings.siteName}
        width={width}
        height={height}
        priority={priority}
        className="block dark:hidden h-auto"
        sizes={`${width}px`}
      />
      {/* Dark theme logo — beyaz yazili, koyu bg uzerinde okunur */}
      <Image
        src={logoDark}
        alt={siteSettings.siteName}
        width={width}
        height={height}
        priority={priority}
        className="hidden dark:block h-auto"
        sizes={`${width}px`}
      />
    </Link>
  )
}
